import {
  missingRequirements,
  type RecipientRequirements,
  type RecipientValues,
} from "./recipient-requirements.ts";

/**
 * The receiver claim experience — product states and the transitions between them.
 *
 * ## What this models, and what it refuses to model
 *
 * This is a **product experience model**. It describes what a person receiving
 * money sees and can do, and nothing else. It is deliberately not a model of
 * claim security.
 *
 * `docs/roadmap/PHASE_03_CLAIM_EXPERIENCE_SCOPE.md` records why. The real claim
 * mechanics — a claim secret, a bearer capability, eligibility, linkage to a
 * transfer, expiry, single-use consumption, routing acceptance — are owned by
 * Phase 02 **PV-16, which is NOT TESTED**, and by the frozen architecture behind
 * it. Inventing UI vocabulary for them here would put words like "expired" and
 * "already claimed" on a screen months before anyone knows what produces them,
 * and those words would then be the specification.
 *
 * So the states below are the ones a *person* experiences:
 *
 * ```
 *                     ┌───────────── cancel ─────────────┐
 *                     ▼                                  │
 *   ready ──claim──▶ recipientDetailsRequired ──submitRecipientDetails──┐
 *     │                                                                │
 *     │                                              confirmingDetails ◀┘
 *     │                                                     │
 *     └── claim (destination already established) ──▶ processing
 *                                                           │
 *                                        ┌──────────────────┴───────────┐
 *                                        ▼                              ▼
 *                                     success                      unavailable
 * ```
 *
 * `cancelled` is reachable from the two states where nothing is in flight, and
 * `unavailable` and `cancelled` both return to `ready` — a person who stopped
 * has not lost the money, and the interface has to be able to say so.
 *
 * ## One claim intent, carried through setup
 *
 * A first-time recipient presses **Claim** once. Collecting the details the bank
 * needs does not cancel that intent and does not ask for it again: the single
 * `submitRecipientDetails` call establishes the destination and then completes
 * the claim, so the flow is two presses rather than three. Re-asking someone to
 * confirm an amount they confirmed ninety seconds earlier, on a screen that has
 * not changed the amount, is friction dressed as diligence.
 *
 * **`success` is reachable only through `processing`, and `processing` only
 * through `claim()` or a `submitRecipientDetails()` that `claim()` led to.**
 * That is a structural guarantee rather than a convention: `CLAUDE.md`
 * § Transaction integrity — "Local UI state cannot manufacture
 * success/settlement" — and a synthetic build is exactly where a success state
 * would otherwise become reachable by accident.
 *
 * ## No hosting assumption
 *
 * Nothing here knows where it is running. There is no URL, no fragment, no query
 * string, no route, no origin and no network client in this module or anywhere
 * under `receiver/`. The eventual production topology is undecided and this code
 * does not quietly choose one — enforced by the `receiver-claim-boundary` gate.
 */

/**
 * Where the money arrives, as the claimant sees it.
 *
 * A label and nothing else. No account number, no partial digits, no rail
 * identifier, no token — `CLAUDE.md` § Privacy keeps every one of those out of
 * this product, and showing invented digits "for visual richness" would train
 * everyone reading the screen to expect them.
 *
 * In production this is the presentational half of an opaque, bank-issued,
 * reusable destination reference: the bank holds the payout information, and
 * hands Nura back a reference plus a label it is safe to show. **Its wire
 * format and backend contract are deliberately not defined here**, because
 * defining them is an integration decision that no UI milestone gets to make.
 */
export interface ReceivingDestination {
  /** A safe synthetic label, e.g. "Primary account". Never digits. */
  readonly label: string;
  /** True once the bank-controlled setup confirmed it in this session. */
  readonly confirmedNow: boolean;
}

/**
 * What the surface is given to present.
 *
 * How much, in what currency, and — via the tenant the surface is rendered with
 * — which institution is behind it. That is the whole list.
 *
 * **There is deliberately no sender here.** An earlier revision carried a
 * synthetic sender label; no sender-identity semantics are authorised yet, and a
 * field that exists "because the screen looked bare" is exactly where a real
 * name would eventually be put. There is also no transfer identifier, no claim
 * reference and no metadata bag for one to be smuggled into later.
 */
export interface ClaimPresentation {
  readonly amountMinor: bigint;
  readonly currency: string;
}

/** How the synthetic provider was asked to end. Never a claim-security outcome. */
export type ClaimOutcome = "completed" | "unavailable";

/**
 * The port a future real integration replaces.
 *
 * **This is not a backend wire contract.** It defines no endpoint, method,
 * header, token, payload, retry policy, authentication scheme or error taxonomy,
 * and it must not grow one: the shape of the real integration depends on
 * decisions that are still open. It exists so that the view layer depends on an
 * interface rather than on a fixture, and so the fixture is the only thing that
 * has to be thrown away.
 */
export interface ClaimProviding {
  /** Amount and currency for this claim. */
  readonly presentation: ClaimPresentation;
  /** What this sending institution needs before it can deliver the money. */
  readonly requirements: RecipientRequirements;
  /** A destination already established for this recipient, if there is one. */
  establishedDestination(): ReceivingDestination | undefined;
  /**
   * Recipient-information collection has completed; establish the destination.
   *
   * **The topology-neutral seam.** Two production shapes satisfy this port
   * without the product UI or the state machine changing, and this milestone
   * chooses neither:
   *
   * - **A — bank-controlled collection.** A component the bank owns collects and
   *   submits the values to the bank's own infrastructure. Nura renders no field
   *   at all, `submission.values` is absent, and the only thing that crosses this
   *   boundary is "collection completed, here is the destination".
   * - **B — direct client-to-bank submission.** The values are collected in the
   *   browser and submitted straight to the sending bank. They exist transiently
   *   in the client and never reach Nura-operated infrastructure.
   *
   * In both, what comes back is the same: a label and, eventually, an opaque
   * bank-issued reference. **No endpoint, payload, encryption format, key,
   * method, authentication scheme or claim contract is defined here**, because
   * every one of those belongs to later architecture work.
   */
  establishDestination(submission: RecipientSubmission): Promise<ReceivingDestination>;
  /** Carry out the claim. The flow decides when; the provider decides how it ends. */
  completeClaim(): Promise<ClaimOutcome>;
}

/**
 * What crosses the collection seam.
 *
 * `values` is **optional, and its absence is the better case**. Under
 * architecture A the bank's own component collected the details and Nura holds
 * none of them, so the submission carries only the fact that collection
 * finished. The synthetic build populates it because its form is the collector —
 * and the values are read to check the configured requirements were met, then
 * dropped. They are never persisted, transmitted, logged, or copied into any
 * state that outlives the call.
 */
export interface RecipientSubmission {
  readonly values?: RecipientValues;
}

/** The product states. Not transfer statuses, and not claim-security states. */
export type ClaimStateName =
  | "ready"
  | "recipientDetailsRequired"
  | "confirmingDetails"
  | "processing"
  | "success"
  | "unavailable"
  | "cancelled";

/** Raised when a transition is attempted from a state that does not allow it. */
export class ClaimFlowError extends Error {
  constructor(message: string) {
    super(message);
    this.name = "ClaimFlowError";
  }
}

/** States a person may act from. Nothing is in flight in any of them. */
const CLAIMABLE: ReadonlySet<ClaimStateName> = new Set(["ready"]);
const CANCELLABLE: ReadonlySet<ClaimStateName> = new Set(["ready", "recipientDetailsRequired"]);
const RESUMABLE: ReadonlySet<ClaimStateName> = new Set(["unavailable", "cancelled"]);

/**
 * The claim flow.
 *
 * One owner for the whole receiver experience, so the amount on the last screen
 * is the amount on the first by construction. The sender flow learned this the
 * hard way — see `reference-transfer.ts`, where two sources of one transfer
 * showed two different amounts — and the receiver flow starts where that one
 * ended up.
 */
export class ClaimFlow {
  #state: ClaimStateName = "ready";
  #destination: ReceivingDestination | undefined;
  readonly #provider: ClaimProviding;
  readonly #listeners = new Set<() => void>();

  constructor(provider: ClaimProviding) {
    this.#provider = provider;
    this.#destination = provider.establishedDestination();
  }

  get state(): ClaimStateName {
    return this.#state;
  }

  get presentation(): ClaimPresentation {
    return this.#provider.presentation;
  }

  /** The destination the money would arrive at, once there is one. */
  get destination(): ReceivingDestination | undefined {
    return this.#destination;
  }

  /** What the sending institution needs before it can deliver the money. */
  get requirements(): RecipientRequirements {
    return this.#provider.requirements;
  }

  /** True when this recipient has to supply receiving details before claiming. */
  get isFirstTime(): boolean {
    return this.#provider.establishedDestination() === undefined;
  }

  subscribe(listener: () => void): () => void {
    this.#listeners.add(listener);
    return () => this.#listeners.delete(listener);
  }

  #enter(state: ClaimStateName): void {
    this.#state = state;
    for (const listener of this.#listeners) listener();
  }

  /**
   * The one action this product has.
   *
   * With a destination already established it claims. Without one it asks for
   * the details the sending bank requires — and the intent to claim stays
   * standing while that happens, which is why nothing here has to be pressed
   * again afterwards.
   */
  async claim(): Promise<void> {
    if (!CLAIMABLE.has(this.#state)) {
      throw new ClaimFlowError(`claim() is not available from ${this.#state}`);
    }

    if (this.#destination === undefined) {
      this.#enter("recipientDetailsRequired");
      return;
    }

    await this.#completeClaim();
  }

  /**
   * Recipient-information collection finished: establish, then finish claiming.
   *
   * One call, three screens. The person pressed Claim, supplied what the bank
   * asked for, and pressed Continue; asking them to press Claim a second time
   * would be the interface forgetting what it was doing.
   *
   * The required-field check is repeated here even though the form has already
   * run it. The state machine must not depend on the interface having been
   * careful, and a provider that received an incomplete submission would be
   * asked to invent a destination out of nothing.
   */
  async submitRecipientDetails(submission: RecipientSubmission): Promise<void> {
    if (this.#state !== "recipientDetailsRequired") {
      throw new ClaimFlowError(`submitRecipientDetails() is not available from ${this.#state}`);
    }

    const values = submission.values;
    if (values !== undefined) {
      const missing = missingRequirements(this.#provider.requirements, values);
      if (missing.length > 0) {
        throw new ClaimFlowError(`missing required recipient details: ${missing.join(", ")}`);
      }
    }

    this.#enter("confirmingDetails");
    // Whatever established the destination — a bank-controlled component or a
    // direct submission — hands back a label and nothing else. No value from the
    // submission is stored here, and `submission` goes out of scope with this
    // call.
    this.#destination = await this.#provider.establishDestination(submission);

    await this.#completeClaim();
  }

  /** The claim itself. Reachable only from `claim()` and from a submission it led to. */
  async #completeClaim(): Promise<void> {
    this.#enter("processing");
    const outcome = await this.#provider.completeClaim();
    // The provider is the only thing that can produce a success. Nothing on the
    // view side can reach this line, which is the point of it being here.
    this.#enter(outcome === "completed" ? "success" : "unavailable");
  }

  /** Step out without claiming. Never available once something is in flight. */
  cancel(): void {
    if (!CANCELLABLE.has(this.#state)) {
      throw new ClaimFlowError(`cancel() is not available from ${this.#state}`);
    }
    this.#enter("cancelled");
  }

  /**
   * Return to the start after a failure or a cancellation.
   *
   * It resumes the *experience*; it does not retry anything, because nothing was
   * attempted. Real retry semantics — idempotency, whether a second attempt is
   * even permitted — are bank and backend concerns that
   * `CLAUDE.md` § Transaction integrity governs and this build does not model.
   */
  resume(): void {
    if (!RESUMABLE.has(this.#state)) {
      throw new ClaimFlowError(`resume() is not available from ${this.#state}`);
    }
    this.#enter("ready");
  }
}
